import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { sanitizeError } from '@heidi/runtime/logger';
import { defineMessages, type MessageDescriptor } from 'react-intl';

// User copy and log context for a rejected stream. Never show or log the
// error's message: it can carry backend text.
export const streamErrorMessages = defineMessages({
  unauthorized: {
    defaultMessage: "We couldn't confirm you're signed in. Try again in a moment.",
    id: 'dNzE4BSRlW',
    description:
      'Error toast body when a streaming request fails with a 401, often an access token that will refresh',
  },
  rateLimited: {
    defaultMessage: 'Too many requests right now. Wait a moment, then try again.',
    id: 'Fr8xVtKbXd',
    description: 'Error toast body when a streaming request fails with a 429',
  },
  serverError: {
    defaultMessage: 'Something went wrong on our side. Try again in a moment.',
    id: 'WYOxIJBUF8',
    description: 'Error toast body when a streaming request fails with a 5xx',
  },
  generic: {
    defaultMessage: 'Something went wrong. Try again.',
    id: 'RIEG6f2xQI',
    description: 'Error toast body when a streaming request fails for another reason',
  },
});

/** The HTTP status a stream or axios error carries, if any. */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { status, response } = error as {
    status?: unknown;
    response?: { status?: unknown };
  };
  if (typeof status === 'number') return status;
  return typeof response?.status === 'number' ? response.status : undefined;
};

export const streamErrorMessage = (status: number | undefined): MessageDescriptor => {
  if (status === 401) return streamErrorMessages.unauthorized;
  if (status === 429) return streamErrorMessages.rateLimited;
  if (status !== undefined && status >= 500) return streamErrorMessages.serverError;
  return streamErrorMessages.generic;
};

/** The toast or inline copy for a rejected stream, outside React. */
export const formatStreamError = (error: unknown): string =>
  formatMessageOutsideReact(streamErrorMessage(httpStatusOf(error)));

// A backend `detail.type` is a class-name slug; anything else in an HTTP
// rejection's message is reported as the generic type.
const ERROR_TYPE_SHAPE = /^[A-Z][A-Za-z]{2,63}Error$/;

const streamErrorType = (error: Error): string =>
  ERROR_TYPE_SHAPE.test(error.message) ? error.message : 'StreamHttpError';

/**
 * A telemetry-safe label: the backend `detail.type` for an HTTP rejection,
 * otherwise only the error's name. Other messages can quote user content.
 */
export const streamErrorClassification = (error: unknown): string => {
  if (!(error instanceof Error)) return typeof error;
  return httpStatusOf(error) === undefined ? error.name : streamErrorType(error);
};

/** Non-sensitive fields to log for a stream error. */
export const streamErrorLogContext = (error: unknown) => {
  const status = httpStatusOf(error);
  return {
    errorName: error instanceof Error ? error.name : typeof error,
    status,
    ...(status !== undefined &&
    error instanceof Error &&
    ERROR_TYPE_SHAPE.test(error.message)
      ? { errorType: error.message }
      : {}),
    ...sanitizeError(error),
  };
};
